import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { config } from '../../config/config';

const bundleTags = (arg) => {
  const tags = [{ type: 'Sales', id: 'LIST' }];
  if (arg?.saleId) tags.push({ type: 'SaleBundle', id: arg.saleId });
  if (arg?.spickingId) tags.push({ type: 'SpickingBundle', id: arg.spickingId });
  return tags;
};

export const pickingApi = createApi({
  reducerPath: 'pickingApi',
  baseQuery: fetchBaseQuery({
    baseUrl: `${config.apiUrl}/picking`,
    prepareHeaders: (headers, { getState }) => {
      const token = getState().auth.user?.token;
      if (token) {
        headers.set('Authorization', `Bearer ${token}`);
      }
      return headers;
    },
  }),
  tagTypes: ['Sales', 'SaleBundle', 'SpickingBundle'],
  endpoints: (builder) => ({
    listSales: builder.query({
      query: (params) => ({
        url: '/sales',
        params,
      }),
      providesTags: [{ type: 'Sales', id: 'LIST' }],
    }),
    listPickingWarehouses: builder.query({
      query: () => '/warehouses',
    }),
    getPickingSettings: builder.query({
      query: () => '/settings',
    }),
    getSaleBundle: builder.query({
      query: (saleId) => `/sales/${saleId}/bundle`,
      providesTags: (result, err, saleId) => [{ type: 'SaleBundle', id: saleId }],
    }),
    getSpickingBundle: builder.query({
      query: (spickingId) => `/spickings/${spickingId}/bundle`,
      providesTags: (result, err, spickingId) => [{ type: 'SpickingBundle', id: spickingId }],
    }),
    startOrResumePicking: builder.mutation({
      query: (saleId) => ({
        url: `/sales/${saleId}/start`,
        method: 'POST',
      }),
      invalidatesTags: (result, err, saleId) => [
        { type: 'Sales', id: 'LIST' },
        { type: 'SaleBundle', id: saleId },
      ],
    }),
    createManualPicking: builder.mutation({
      query: (warehouseId) => ({
        url: '/spickings',
        method: 'POST',
        body: warehouseId ? { warehouseId } : {},
      }),
      invalidatesTags: [{ type: 'Sales', id: 'LIST' }],
    }),
    addPickingLine: builder.mutation({
      query: ({ spickingId, productId, quantity }) => ({
        url: `/spickings/${spickingId}/lines`,
        method: 'POST',
        body: { productId, quantity },
      }),
      invalidatesTags: (result, err, arg) => bundleTags(arg),
    }),
    setPickerBarcode: builder.mutation({
      query: ({ spickingId, barcode, replace }) => ({
        url: `/spickings/${spickingId}/picker-barcode`,
        method: 'POST',
        body: { barcode, replace: Boolean(replace) },
      }),
      invalidatesTags: (result, err, arg) => bundleTags(arg),
    }),
    getPickingCatalogBrowse: builder.query({
      query: ({ spickingId, parentId, search }) => ({
        url: `/spickings/${spickingId}/catalog`,
        params: { parentId, search },
      }),
    }),
    updateLineQuantity: builder.mutation({
      query: ({ spickingId, lineId, quantity }) => ({
        url: `/spickings/${spickingId}/lines/${lineId}`,
        method: 'PATCH',
        body: { quantity },
      }),
      invalidatesTags: (result, err, arg) => bundleTags(arg),
    }),
    confirmLineStock: builder.mutation({
      query: ({ spickingId, lineId, confirmed }) => ({
        url: `/spickings/${spickingId}/lines/${lineId}/stock-confirm`,
        method: 'PATCH',
        body: { confirmed },
      }),
      invalidatesTags: (result, err, arg) => bundleTags(arg),
    }),
    completePicking: builder.mutation({
      query: ({ spickingId, confirmIncomplete }) => ({
        url: `/spickings/${spickingId}/complete`,
        method: 'POST',
        body: { confirmIncomplete: Boolean(confirmIncomplete) },
      }),
      invalidatesTags: (result, err, arg) => bundleTags(arg),
    }),
    reopenPicking: builder.mutation({
      query: ({ spickingId }) => ({
        url: `/spickings/${spickingId}/reopen`,
        method: 'POST',
      }),
      invalidatesTags: (result, err, arg) => bundleTags(arg),
    }),
    deleteOrphanLine: builder.mutation({
      query: ({ spickingId, lineId }) => ({
        url: `/spickings/${spickingId}/lines/${lineId}`,
        method: 'DELETE',
      }),
      invalidatesTags: (result, err, arg) => bundleTags(arg),
    }),
    markSaleDeleted: builder.mutation({
      query: (saleId) => ({
        url: `/sales/${saleId}/mark-deleted`,
        method: 'POST',
      }),
      invalidatesTags: (result, err, saleId) => [
        { type: 'Sales', id: 'LIST' },
        { type: 'SaleBundle', id: saleId },
      ],
    }),
    markSpickingDeleted: builder.mutation({
      query: (spickingId) => ({
        url: `/spickings/${spickingId}/mark-deleted`,
        method: 'POST',
      }),
      invalidatesTags: (result, err, spickingId) => [
        { type: 'Sales', id: 'LIST' },
        { type: 'SpickingBundle', id: spickingId },
      ],
    }),
  }),
});

export const {
  useListSalesQuery,
  useListPickingWarehousesQuery,
  useGetPickingSettingsQuery,
  useGetSaleBundleQuery,
  useGetSpickingBundleQuery,
  useStartOrResumePickingMutation,
  useCreateManualPickingMutation,
  useAddPickingLineMutation,
  useSetPickerBarcodeMutation,
  useGetPickingCatalogBrowseQuery,
  useUpdateLineQuantityMutation,
  useConfirmLineStockMutation,
  useCompletePickingMutation,
  useReopenPickingMutation,
  useDeleteOrphanLineMutation,
  useMarkSaleDeletedMutation,
  useMarkSpickingDeletedMutation,
} = pickingApi;
